import React, {
  useRef,
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
} from "react";
import ReactDOM from "react-dom";
import {
  ChildrenContainer,
  StyledTooltip,
  TooltipArrow,
  TooltipPositionEnum,
  TooltipVariantEnum,
} from "./Tooltip.style";

export { TooltipVariantEnum } from "./Tooltip.style";
import { FontWeightEnum } from "../../../types/AppTypes";
import { BodyTyp } from "../../Typography/Typography";
import { theme } from "../../../constants/StyledComponents";

export enum TooltipOpenModeEnum {
  CLICK = "click",
  HOVER = "hover",
}

export const Tooltip = ({
  message,
  children,
  hide,
  width,
  variant,
  position = TooltipPositionEnum.TOP,
  autoWidth = false,
  openMode = TooltipOpenModeEnum.HOVER,
}: {
  message: string | React.ReactNode;
  children: React.ReactNode;
  hide?: boolean;
  width?: number;
  variant?: TooltipVariantEnum;
  position?: TooltipPositionEnum;
  autoWidth?: boolean;
  openMode?: TooltipOpenModeEnum;
}) => {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const [arrowOffset, setArrowOffset] = useState(0);

  const showTooltip = () => {
    if (!hide) setVisible(true);
  };
  const hideTooltip = () => setVisible(false);

  useEffect(() => {
    if (hide) {
      setVisible(false);
    }
  }, [hide]);

  // the trigger can be covered by portaled content (eg. a Select dropdown),
  // so mouseleave alone is not enough to dismiss the tooltip
  useEffect(() => {
    if (openMode === TooltipOpenModeEnum.CLICK || !visible) {
      return;
    }

    const dismissIfOutside = (e: Event) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        hideTooltip();
      }
    };

    document.addEventListener("pointerdown", dismissIfOutside, true);
    document.addEventListener("focusin", dismissIfOutside, true);
    return () => {
      document.removeEventListener("pointerdown", dismissIfOutside, true);
      document.removeEventListener("focusin", dismissIfOutside, true);
    };
  }, [visible, openMode]);

  const updateTooltipPosition = useCallback(() => {
    if (!containerRef.current || !tooltipRef.current) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const tooltipHeight = tooltipRef.current.offsetHeight;
    const tooltipWidth = tooltipRef.current.offsetWidth;
    const TOOLTIP_VIEWPORT_MARGIN = 24;

    if (position === TooltipPositionEnum.RIGHT) {
      const maxLeft =
        window.innerWidth - tooltipWidth - TOOLTIP_VIEWPORT_MARGIN;
      const left = Math.max(
        TOOLTIP_VIEWPORT_MARGIN,
        Math.min(containerRect.right + 16, maxLeft),
      );
      setArrowOffset(0);
      setTooltipPosition({
        top:
          containerRect.top +
          containerRef.current.offsetHeight / 2 -
          tooltipHeight / 2,
        left,
      });
      return;
    }

    const desiredCenter =
      containerRect.left + containerRef.current.offsetWidth / 2;
    const halfWidth = tooltipWidth / 2;
    const minCenter = halfWidth + TOOLTIP_VIEWPORT_MARGIN;
    const maxCenter = window.innerWidth - halfWidth - TOOLTIP_VIEWPORT_MARGIN;
    const center = Math.min(
      Math.max(desiredCenter, minCenter),
      Math.max(maxCenter, minCenter),
    );
    setArrowOffset(desiredCenter - center);

    if (position === TooltipPositionEnum.BOTTOM) {
      setTooltipPosition({
        top: containerRect.bottom + 16,
        left: center,
      });
      return;
    }

    setTooltipPosition({
      top: containerRect.top - tooltipHeight - 16,
      left: center,
    });
  }, [position]);

  useLayoutEffect(() => {
    if (!visible || hide) {
      return;
    }

    updateTooltipPosition();

    const rafId = requestAnimationFrame(() => {
      updateTooltipPosition();
      requestAnimationFrame(updateTooltipPosition);
    });

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateTooltipPosition)
        : null;

    if (tooltipRef.current) {
      resizeObserver?.observe(tooltipRef.current);
    }
    if (containerRef.current) {
      resizeObserver?.observe(containerRef.current);
    }

    window.addEventListener("scroll", updateTooltipPosition, true);
    window.addEventListener("resize", updateTooltipPosition);

    return () => {
      cancelAnimationFrame(rafId);
      resizeObserver?.disconnect();
      window.removeEventListener("scroll", updateTooltipPosition, true);
      window.removeEventListener("resize", updateTooltipPosition);
    };
  }, [visible, hide, updateTooltipPosition, message]);

  const handleClick = () => setVisible((prev) => !prev);

  useEffect(() => {
    if (openMode === TooltipOpenModeEnum.CLICK && visible) {
      const handleOutsideClick = (e: MouseEvent) => {
        if (
          !containerRef.current?.contains(e.target as Node) &&
          !tooltipRef.current?.contains(e.target as Node)
        ) {
          hideTooltip();
        }
      };
      document.addEventListener("mousedown", handleOutsideClick);
      return () =>
        document.removeEventListener("mousedown", handleOutsideClick);
    }
  }, [visible, openMode]);

  return (
    <ChildrenContainer
      onMouseEnter={
        openMode === TooltipOpenModeEnum.CLICK ? undefined : showTooltip
      }
      onMouseLeave={
        openMode === TooltipOpenModeEnum.CLICK ? undefined : hideTooltip
      }
      onBlur={openMode === TooltipOpenModeEnum.CLICK ? undefined : hideTooltip}
      onClick={openMode === TooltipOpenModeEnum.CLICK ? handleClick : undefined}
      ref={containerRef}
      $openMode={openMode}
    >
      {children}
      {visible &&
        !hide &&
        ReactDOM.createPortal(
          <StyledTooltip
            ref={tooltipRef}
            $top={tooltipPosition.top}
            $left={tooltipPosition.left}
            $width={width}
            $variant={variant}
            $position={position}
            $autoWidth={autoWidth}
          >
            {typeof message === "string" ? (
              <BodyTyp
                variant="2"
                fontWeight={FontWeightEnum.REGULAR}
                color={
                  variant === TooltipVariantEnum.DARK
                    ? theme.colors.white
                    : theme.colors.black
                }
              >
                {message}
              </BodyTyp>
            ) : (
              message
            )}
            <TooltipArrow
              $variant={variant}
              $position={position}
              style={
                arrowOffset !== 0
                  ? { left: `calc(50% - 7.5px + ${arrowOffset}px)` }
                  : undefined
              }
            />
          </StyledTooltip>,
          document.body,
        )}
    </ChildrenContainer>
  );
};
